"use client";

import type { JSX } from "react";
import { Icon, Link, Text, View } from "@/components";
import { useLayout } from "@/providers";
import type { ModuleStatus } from "@/types/elearning";

/** Fila de módulo ya resuelta por la página (progreso + título del curso). */
export interface ModuleListItem {
  moduleId: string;
  order: number;
  /** Nombre visible ("Introducción", "Módulo 1"); sin él se muestra el número. */
  displayName?: string | null;
  title: string;
  status: ModuleStatus;
  videoCompleted: boolean;
  quizPassed: boolean;
  /** Si el alumno ya entró al módulo alguna vez (decide "Iniciar" vs "Continuar"). */
  started: boolean;
  /** Destino al entrar al módulo; solo aplica si no está bloqueado. */
  href: string;
}

export interface ModuleListProps {
  items: ModuleListItem[];
  /** Si el curso usa quiz por módulo (condiciona el estado que se muestra). */
  muestraQuiz: boolean;
  /**
   * Si el curso restringe adelantar el video. Sin la restricción el quiz no
   * espera el video completo (§7.2), y el hint pasa a "quiz pendiente" apenas
   * el quiz queda pendiente. Por defecto `true` (mientras el curso carga).
   */
  restringeAvance?: boolean;
}

/**
 * @name ModuleList
 * @description Lista de módulos de la cursada (A4) con su estado de acceso.
 * Refleja el avance secuencial que resuelve el servidor: el candado y su motivo
 * se muestran de forma explícita ("se habilita al completar el módulo anterior"),
 * nunca como error oculto. La lista no calcula desbloqueos: solo los refleja.
 *
 * @param {ModuleListProps} props - Filas resueltas y configuración del curso.
 * @returns {JSX.Element} La lista renderizada.
 */
const ModuleList = ({
  items,
  muestraQuiz,
  restringeAvance = true
}: ModuleListProps): JSX.Element => {
  const { t } = useLayout();

  const renderEstado = (item: ModuleListItem): JSX.Element => {
    if (item.status === "completed") {
      return (
        <Text
          type="span"
          className="text-xs font-medium text-green-600 dark:text-green-400 flex items-center gap-1.5"
        >
          <Icon name="fa-solid fa-circle-check" className="text-xs!" />
          {t("moduleComplete")}
        </Text>
      );
    }
    if (item.status === "locked") {
      return (
        <Text
          type="span"
          className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"
        >
          <Icon name="fa-solid fa-lock" className="text-xs!" />
          {t("moduleLockedShort")}
        </Text>
      );
    }
    // Desbloqueado pero sin abrir: "En curso" contradecia al CTA, que ofrece
    // iniciarlo. Va en neutro, no en primary: todavia no hay avance que destacar.
    if (!item.started) {
      return (
        <Text
          type="span"
          className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"
        >
          <Icon name="fa-regular fa-circle" className="text-xs!" />
          {t("moduleNotStarted")}
        </Text>
      );
    }
    return (
      <Text
        type="span"
        className="text-xs font-medium text-primary flex items-center gap-1.5"
      >
        <Icon name="fa-solid fa-circle-play" className="text-xs!" />
        {muestraQuiz &&
        (item.videoCompleted || !restringeAvance) &&
        !item.quizPassed
          ? t("pendingQuiz")
          : t("inProgress")}
      </Text>
    );
  };

  return (
    <View type="ul" className="flex flex-col">
      {items.map((item) => {
        const locked = item.status === "locked";
        return (
          <View
            key={item.moduleId}
            type="li"
            className={`flex items-center gap-4 py-3 px-2 border-b border-border last:border-b-0 ${
              locked ? "opacity-60" : ""
            }`}
          >
            {/* El circulo del modulo en curso va navy solido en oscuro, con el
                numero en blanco (13,7:1): el dorado sobre dorado al 10% daba
                4,3:1 contra la tarjeta azul, por debajo de AA para este tamano. */}
            <Text
              type="span"
              className={`flex items-center justify-center min-w-8 h-8 px-2.5 rounded-full text-xs font-semibold shrink-0 whitespace-nowrap ${
                item.status === "completed"
                  ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                  : "bg-primary/10 text-primary dark:bg-(--sv-navy) dark:text-white"
              }`}
            >
              {item.displayName?.trim() || item.order}
            </Text>
            <View className="flex flex-col gap-0.5 min-w-0 flex-1">
              <Text className="text-sm font-medium text-foreground truncate">
                {item.title}
              </Text>
              {renderEstado(item)}
              {locked && (
                <Text type="span" className="text-xs text-muted-foreground">
                  {t("moduleLockedReason")}
                </Text>
              )}
            </View>
            {!locked && (
              <Link
                href={item.href}
                className="shrink-0 inline-flex items-center min-h-11 sm:min-h-0 text-sm font-medium text-primary hover:underline"
              >
                {item.status === "completed"
                  ? t("review")
                  : item.started
                    ? t("continue")
                    : t("startModule")}
              </Link>
            )}
          </View>
        );
      })}
    </View>
  );
};

export default ModuleList;
